import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  GlobalStyles,
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Breadcrumbs,
  Link,
  Button,
  Skeleton,
  alpha,
} from '@mui/material';
import {
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  KeyboardArrowDown,
  NavigateNext,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useCapabilities } from './contexts/CapabilitiesContext';
import { motion, AnimatePresence } from 'framer-motion';
import { getCalAvatar } from './config/calAvatars';

// Modern theme and components
import { createModernTheme } from './theme/modernTheme';
import ModernSidebar from './components/ModernSidebar';
import FontSizeControl, { clampFontScale } from './components/FontSizeControl';
import ModernDashboard from './components/ModernDashboard';
import MeterFactorControlCharts from './components/MeterFactorControlCharts';
import ModernFileUpload from './components/ModernFileUpload';

// Existing components to integrate
import DataViewer from './components/DataViewer';
import DocumentUploadComponent from './components/PDFUploadComponent';
import UserHistoryTable from './components/UserHistoryTable';
import ModernCalChat from './components/ModernCalChat';
import CalNotifications from './components/CalNotifications';
import NotificationCenter from './components/NotificationCenter';
import UserPreferences from './components/UserPreferences';
import CalComplianceTab from './components/CalComplianceTab';
import VerificationQueue from './components/VerificationQueue';
import RegressionUtility from './components/RegressionUtility';
import MassBalanceHub from './components/MassBalanceHub';
import CostOfNonCompliance from './components/CostOfNonCompliance';
import { CalWebSocketProvider } from './contexts/CalWebSocketContext';
import { CalChatProvider } from './contexts/CalChatContext';
import { UploadQueueManager } from './utils/UploadQueueManager';
import { ProcessingResult } from './types';
import './App.css';

function App() {
  const {
    isLoading,
    isAuthenticated,
    error,
    loginWithRedirect: login,
    logout: auth0Logout,
    user,
    getIdTokenClaims,
  } = useAuth0();
  const { can } = useCapabilities();  // RBAC gating (inert while frontend RBAC_ENFORCED=false)

  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const [processingResult, setProcessingResult] = useState<ProcessingResult | null>(null);
  const [showData, setShowData] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [refreshHistory, setRefreshHistory] = useState(0);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [uploadManager, setUploadManager] = useState<UploadQueueManager | undefined>(undefined);
  const [selectedMeter, setSelectedMeter] = useState<string | undefined>(undefined);

  // Modern UI states
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedTab, setSelectedTab] = useState('dashboard');
  // Set to Date.now() when a Settings save triggers a fleet recalculation, so the
  // dashboard shows its recalculating cue after Settings closes.
  const [bgRefreshSignal, setBgRefreshSignal] = useState(0);

  // Whole-app scale (accessibility). Text scales via the root font-size, and the theme
  // scales spacing + breakpoints so padding/margins grow and grids reflow to fewer
  // columns — everything grows together and reflows, with no horizontal overflow.
  // Persisted per user in localStorage so it's remembered on their next login here.
  const [fontScale, setFontScale] = useState(1);
  const theme = createModernTheme(mode, fontScale);
  const fontScaleLoaded = useRef(false);
  useEffect(() => {
    if (!user?.email) return;
    const saved = parseFloat(localStorage.getItem(`fontScale:${user.email}`) || '');
    fontScaleLoaded.current = true;
    setFontScale(isFinite(saved) ? clampFontScale(saved) : 1);
  }, [user?.email]);
  useEffect(() => {
    // Scale text via the ROOT font-size (rem). This never changes layout widths, so it
    // can't overflow the viewport the way `zoom` did (zoom scaled containers but not the
    // media-query viewport, so responsive grids kept their column count and ran off the
    // right at high scale). Here text and rem-based MUI components grow and wrap in place.
    const root = document.documentElement;
    const body = document.body;
    // Clear any earlier zoom-based attempts.
    (root.style as any).zoom = '';
    (body.style as any).zoom = '';
    root.style.removeProperty('width');
    body.style.removeProperty('width');
    if (fontScale === 1) {
      root.style.removeProperty('font-size');
    } else {
      root.style.setProperty('font-size', `${16 * fontScale}px`, 'important');
    }
    if (fontScaleLoaded.current && user?.email) {
      localStorage.setItem(`fontScale:${user.email}`, String(fontScale));
    }
  }, [fontScale, user?.email]);

  // Callbacks
  const handleMultiFileComplete = useCallback((fileId: string, reportIds?: string[]) => {
    console.log(`File ${fileId} completed with reports:`, reportIds);
    setRefreshHistory(prev => prev + 1);
  }, []);

  const handleLegacyFileComplete = useCallback((fileId: string, reportId?: string) => {
    console.log(`File ${fileId} completed with report ${reportId}`);
    setRefreshHistory(prev => prev + 1);
  }, []);

  const handleRefreshHistory = useCallback(() => {
    setRefreshHistory(prev => prev + 1);
  }, []);

  const logout = () => {
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const handleViewReport = async (reportId: string) => {
    setSelectedReportId(reportId);
    setShowData(true);

    const tokenClaims = await getIdTokenClaims();
    const idToken = tokenClaims?.__raw;

    fetch(`${process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod'}/report/${reportId}`, {
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    })
      .then(res => res.json())
      .then(data => {
        setProcessingResult({
          status: 'processed',
          reportId: reportId,
          reportData: data,
          s3Key: data.s3_key || ''
        });
      })
      .catch(err => console.error('Error fetching report:', err));
  };

  // Initialize upload manager
  useEffect(() => {
    if (isAuthenticated && !uploadManager && user?.email) {
      const manager = new UploadQueueManager(
        process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod',
        user.email,
        {
          onComplete: handleLegacyFileComplete,
        },
        async () => {
          const tokenClaims = await getIdTokenClaims();
          return tokenClaims?.__raw || '';
        },
        getIdTokenClaims
      );
      setUploadManager(manager);
    }
  }, [isAuthenticated, uploadManager, user?.email, getIdTokenClaims, handleLegacyFileComplete]);

  // Modern loading screen
  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ mb: 3 }}>
                <Skeleton variant="circular" width={80} height={80} sx={{ mx: 'auto' }} />
              </Box>
              <Skeleton variant="text" width={200} height={32} sx={{ mx: 'auto', mb: 1 }} />
              <Skeleton variant="text" width={150} height={24} sx={{ mx: 'auto' }} />
            </Box>
          </motion.div>
        </Box>
      </ThemeProvider>
    );
  }

  // Modern login screen
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            bgcolor: 'background.default',
          }}
        >
          {/* Left side - Login form */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{ width: '100%', maxWidth: '400px' }}
            >
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <img
                    src={getCalAvatar().path}
                    alt="Cal Logo"
                    style={{
                      height: '280px',
                      width: '280px',
                      marginRight: '25px',
                      objectFit: 'contain',
                      borderRadius: '50%',
                      backgroundColor: '#f5f5f5',
                      padding: '12px'
                    }}
                  />
                  <Typography
                    variant="h1"
                    sx={{
                      fontFamily: '"Bustani", "Helvetica Neue", "Helvetica", -apple-system, sans-serif',
                      fontWeight: 400,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(
                        theme.palette.primary.main,
                        0.7
                      )} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontSize: '5rem',
                      letterSpacing: '12px',
                      lineHeight: 1,
                      textTransform: 'uppercase'
                    }}
                  >
                    CAL
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
                  Your AI Loss Control Partner
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem', px: 2 }}>
                  Intelligent Loss Control that continuously monitors performance,
                  validates data, and highlights emerging risks
                </Typography>
              </Box>

              {error && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <Box
                    sx={{
                      p: 2,
                      mb: 3,
                      borderRadius: '8px',
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    }}
                  >
                    <Typography variant="body2" color="error">
                      {error.message}
                    </Typography>
                  </Box>
                </motion.div>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => login()}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: theme.shadows[3],
                  },
                }}
              >
                Sign In with Auth0
              </Button>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Protected by enterprise-grade security
                </Typography>
              </Box>
            </motion.div>
          </Box>

          {/* Right side - Brand panel */}
          <Box
            sx={{
              flex: 1,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                background:
                  'radial-gradient(circle at 20% 50%, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 70%)',
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              style={{
                zIndex: 1,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '2rem'
              }}
            >
              <img
                src="/trindent-logo-h-white.png"
                alt="Trindent Consulting"
                style={{
                  maxHeight: '140px',
                  width: 'auto',
                  marginBottom: '48px',
                  objectFit: 'contain'
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  mb: 3,
                  fontSize: '2rem',
                  letterSpacing: '0.5px'
                }}
              >
                Consulting Solutions that Make it Happen™
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 400,
                  maxWidth: 500,
                  lineHeight: 1.7,
                  fontSize: '1.1rem'
                }}
              >
                Trindent Consulting is a global technical augmentation company
                with a passion for solving complex problems
              </Typography>
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.2)', width: '80%' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '0.9rem',
                    fontStyle: 'italic'
                  }}
                >
                  Embedded expertise that ensures critical operational knowledge
                  remains accessible, actionable, and trusted
                </Typography>
              </Box>
            </motion.div>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  // Helper function to render content based on selected tab
  // RBAC route guard (§9): a hidden tool must also be unreachable via a typed/forced tab.
  // Maps the gated tabs to their capability; inert while can() returns true (Phase 1).
  const TAB_CAPABILITY: Record<string, string> = {
    'regression-utility': 'use_regression_utility',
    'mass-balance': 'use_mass_balance',
    'upload': 'upload_documents',
    'verification': 'review_reports',
  };

  const renderContent = () => {
    const requiredCap = TAB_CAPABILITY[selectedTab];
    if (requiredCap && !can(requiredCap)) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" gutterBottom>Access restricted</Typography>
          <Typography color="text.secondary">
            You don't have permission to view this. Ask an admin if you need access.
          </Typography>
        </Box>
      );
    }
    switch (selectedTab) {
      case 'dashboard':
        return <ModernDashboard userEmail={user?.email} externalRefreshSignal={bgRefreshSignal} />;

      case 'meter-control-charts':
        return <MeterFactorControlCharts userEmail={user?.email} />;

      case 'upload':
        return (
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
              Upload Documents
            </Typography>
            {/* Use HTTP sequential upload to avoid API Gateway rate limits */}
            <DocumentUploadComponent
              onFileComplete={handleMultiFileComplete}
              onRefreshHistory={handleRefreshHistory}
            />
          </Box>
        );

      case 'history':
        return (
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
              Document History
            </Typography>
            <UserHistoryTable
              userEmail={user?.email || ''}
              apiEndpoint={process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod'}
              refreshTrigger={refreshHistory}
              onViewReport={handleViewReport}
              uploadManager={uploadManager}
            />
          </Box>
        );

      case 'analytics-overview':
      case 'analytics-meters':
      case 'analytics-trends':
        return (
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
              Analytics
            </Typography>
            {showData && processingResult?.reportData && (
              <DataViewer
                reportData={processingResult.reportData}
              />
            )}
            {!showData && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  Select a document from the history to view analytics
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 'verification':
        return <VerificationQueue />;

      case 'compliance':
        return <CalComplianceTab />;

      case 'cost-compliance':
        return <CostOfNonCompliance />;

      case 'regression-utility':
        return <RegressionUtility />;

      case 'mass-balance':
        return <MassBalanceHub />;

      case 'notifications':
        return <NotificationCenter onMeterSelect={setSelectedMeter} />;

      case 'settings':
        return (
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
              Settings
            </Typography>
            <UserPreferences
              open={true}
              onClose={() => setSelectedTab('dashboard')}
              userEmail={user?.email || ''}
              onRefreshTriggered={() => setBgRefreshSignal(Date.now())}
            />
          </Box>
        );

      default:
        return <ModernDashboard userEmail={user?.email} />;
    }
  };

  // Main app with modern layout
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          // Absolutely prevent horizontal scrolling
          'html, body': {
            overflowX: 'hidden !important',
            width: '100% !important',
            maxWidth: '100% !important',
            margin: 0,
            padding: 0,
          },
          '#root': {
            overflowX: 'hidden !important',
            width: '100% !important',
            maxWidth: '100% !important',
          },
          // Prevent any element from exceeding viewport
          '*': {
            boxSizing: 'border-box !important',
          },
          // Constrain all flex containers
          '[style*="display: flex"], .MuiBox-root, .MuiGrid-root': {
            maxWidth: '100% !important',
          },
          // Constrain toolbar content
          '.MuiToolbar-root': {
            maxWidth: '100% !important',
            overflowX: 'hidden !important',
          },
          // Constrain breadcrumbs
          '.MuiBreadcrumbs-root': {
            maxWidth: '100% !important',
            overflow: 'hidden !important',
          }
        }}
      />
      <CalChatProvider>
        <CalWebSocketProvider>
          <Box sx={{
            display: 'flex',
            height: '100%',
            bgcolor: 'background.default',
            width: '100%',
            overflow: 'hidden',
            position: 'relative'
          }}>
          {/* Modern Sidebar */}
          <ModernSidebar
            open={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
            uploadCount={uploadManager?.getQueueLength() || 0}
          />

          {/* Main Content */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Modern App Bar */}
            <AppBar position="sticky" elevation={0}>
              <Toolbar sx={{
                px: { xs: 1, sm: 2, md: 3 },
                minHeight: '64px',
                overflow: 'hidden'
              }}>
                {/* Breadcrumbs */}
                <Breadcrumbs
                  separator={<NavigateNext fontSize="small" />}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    mr: 1
                  }}
                >
                  <Link
                    color="inherit"
                    href="#"
                    onClick={() => setSelectedTab('dashboard')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Dashboard
                  </Link>
                  {selectedTab !== 'dashboard' && (
                    <Typography color="text.primary" sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '200px'
                    }}>
                      {selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1).replace(/-/g, ' ')}
                    </Typography>
                  )}
                </Breadcrumbs>

                {/* Right side actions */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.5, sm: 1 },
                  flexShrink: 1,
                  ml: 1,
                  minWidth: 0,
                  overflow: 'hidden'
                }}>
                  {/* Cal Notifications */}
                  <CalNotifications onMeterSelect={setSelectedMeter} />

                  {/* Text size (accessibility) */}
                  <FontSizeControl scale={fontScale} onChange={setFontScale} />

                  {/* Theme Toggle */}
                  <IconButton
                    onClick={toggleColorMode}
                    size="small"
                    sx={{
                      color: theme.palette.text.primary,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      },
                    }}
                  >
                    {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                  </IconButton>

                  {/* User Menu */}
                  <Button
                    onClick={handleUserMenuOpen}
                    startIcon={
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: theme.palette.primary.main,
                        }}
                      >
                        {user?.email?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                    }
                    endIcon={<KeyboardArrowDown />}
                    sx={{
                      ml: 1,
                      color: theme.palette.text.primary,
                      textTransform: 'none',
                      minWidth: 'auto'
                    }}
                  >
                    <Box sx={{
                      textAlign: 'left',
                      maxWidth: '150px',
                      overflow: 'hidden'
                    }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {user?.name || user?.email?.split('@')[0] || 'User'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{
                        display: { xs: 'none', sm: 'block' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {user?.email || ''}
                      </Typography>
                    </Box>
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleUserMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    PaperProps={{
                      elevation: 8,
                      sx: {
                        mt: 1,
                        minWidth: 200,
                        borderRadius: 2,
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                        '& .MuiMenuItem-root': {
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                          mx: 0.5,
                          my: 0.25,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          },
                        },
                        '&:before': {
                          content: '""',
                          display: 'block',
                          position: 'absolute',
                          top: 0,
                          right: 14,
                          width: 10,
                          height: 10,
                          bgcolor: 'background.paper',
                          transform: 'translateY(-50%) rotate(45deg)',
                          zIndex: 0,
                        },
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleUserMenuClose();
                        setSelectedTab('settings');
                      }}
                    >
                      Settings
                    </MenuItem>
                    <MenuItem onClick={logout}>Sign Out</MenuItem>
                  </Menu>
                </Box>
              </Toolbar>
            </AppBar>

            {/* Page Content with animations */}
            <Box sx={{
              flex: 1,
              p: 3,
              overflowY: 'auto',
              overflowX: 'hidden',
              minWidth: 0,
              boxSizing: 'border-box'
            }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  style={{ width: '100%', overflow: 'hidden' }}
                >
                  {renderContent()}
                </motion.div>
              </AnimatePresence>
            </Box>
          </Box>
        </Box>

        {/* Modern Cal Chat - Outside main container for fixed positioning */}
        <ModernCalChat
          userEmail={user?.email || ''}
          selectedMeter={selectedMeter}
          onMeterSelect={setSelectedMeter}
        />
        </CalWebSocketProvider>
      </CalChatProvider>
    </ThemeProvider>
  );
}

export default App;